import { BaseApiClient } from './BaseApiClient';
import { IApiInterface } from './IApiConfig';
import { IAuthTokenBuilder } from './IAuthTokenBuilder';

export class UserServiceClient extends BaseApiClient {
  private logger: any;

  public constructor(
    config: IApiInterface,
    fetch: any,
    logger: any,
    authTokenBuilder: IAuthTokenBuilder | undefined,
    googleAnalytics?: any,
  ) {
    super(config, fetch, authTokenBuilder, googleAnalytics);
    this.logger = logger || (() => {
      // empty
    });
  }

  public async signupForPreAccount(createUserData: any) {
    this.logger('[UserServiceClient] signupForPreAccount');
    const request = await this.buildPostRequest('api/signup', createUserData);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async continueSignup(token: string, legalAgreements: string[]) {
    this.logger(`[UserServiceClient] continue signup with token ${token}`);
    const request = await this.buildPutRequest(`api/signup/${token}`, { legalAgreements });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async completeDemoStudioPreAccount(token: string) {
    this.logger(`[UserServiceClient] continue signup with token ${token}`);
    const request = await this.buildPutRequest(`api/signup/demo/studio-workout/${token}`, {});
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getUserBySignupToken(token: string) {
    this.logger(`[UserServiceClient] continue signup with token ${token}`);
    const request = await this.buildGetRequest(`api/signup/${token}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getUserPlatformLink(musicPlatform: string) {
    this.logger(`[UserServiceClient] Taking user to : ${musicPlatform}`);
    const request = await this.buildGetRequest(`api/music-platforms/${musicPlatform}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    this.logger(`[UserServiceClient] user music platform link : ${data}`);
    return data;
  }

  public async getUserMessengerPlatformLink(messagingPlatform: string) {
    this.logger(`[UserServiceClient] Taking user to : ${messagingPlatform}`);
    const request = await this.buildGetRequest(`api/messaging-platforms/${messagingPlatform}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    this.logger(`[UserServiceClient] user messaging platform link : ${data}`);
    return data;
  }

  public async getUserForPreAccountByEmail(email: string) {
    this.logger(`[UserServiceClient] getting pre signup token for email: ${email}`);
    const request = await this.buildGetRequest(`api/signup/preAccountToken/${email}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResultOrNullFor404(result);
    return data;
  }

  public async createUser(user: any) {
    this.logger(`[UserServiceClient] create user for uid ${user.uid}`);
    const request = await this.buildPostRequest('api/users/me', user);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async updateUser(user: any, updatedAtOnboardingComplete = false) {
    this.logger(`[UserServiceClient] update user for uid ${user.uid}`);
    const path = updatedAtOnboardingComplete
      ? `api/users/me?updatedAtOnboardingComplete=${updatedAtOnboardingComplete}`
      : 'api/users/me';
    const request = await this.buildPutRequest(path, {
      userProfile: user,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async updateBasicUserInfoAfterInitialAuthSetup(user: any) {
    this.logger(`[UserServiceClient] update user for uid ${user.uid}`);
    const request = await this.buildPutRequest('api/users/me/afterInitialAuth', user);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async setProfileAsPrivate(isPrivate: boolean) {
    this.logger(`[UserServiceClient] setting user profile as private? ${isPrivate}`);
    const request = await this.buildPutRequest('api/users/me/privateProfile', {
      isPrivate,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async removeUserSignupWorkoutInvite() {
    this.logger('[UserServiceClient] removing user workout invite');
    const request = await this.buildDeleteRequest('api/users/me/removeSignupWorkoutInvite');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getAuthenticatedUser() {
    const request = await this.buildGetRequest('api/users/me');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getUserEnabledFeatures() {
    const request = await this.buildGetRequest('api/users/me/features');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async validateUserIsAllowedToContinueAuthFlow(authType: string, isNewUser: boolean) {
    this.logger(`[UserServiceClient] validating user can continue auth authType=${authType}, isNewUser=${isNewUser}`);
    const request = await this.buildPostRequest('api/users/canContinueAuthFlow', {
      authType,
      isNewUser,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getOtherUser(uid: string) {
    this.logger(`[UserServiceClient] get another user's details ${uid}`);
    const request = await this.buildGetRequest(`api/users/${uid}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getOtherUsersBase64Avatar(uid: string) {
    this.logger(`[UserServiceClient] get another user's avatar ${uid}`);
    const request = await this.buildGetRequest(`api/users/${uid}/avatar.base64`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public getInfluencerAvatar = async (uid: string) => {
    const request = await this.buildGetRequest(`api/influencers/${uid}/avatar.base64`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public async getPaymentInfo(uid: string) {
    this.logger(`[UserServiceClient] get payment info uid: ${uid}`);
    const request = await this.buildGetRequest('api/users/me/paymentInfo');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getSubscriptionPaymentSummary(
    userId: string,
    planId?: string,
    coupon?: string,
    onceoff?: string,
    pendingPlanId?: string,
    pendingCoupon?: string,
    stripeCustomerId?: string,
  ) {
    this.logger(`[UserServiceClient] get plan details for ${planId} and coupon ${coupon}`);
    const params = new URLSearchParams({
      ...onceoff ? { onceoff } : {},
      ...planId ? { planId } : {},
      ...coupon ? { coupon } : {},
      ...pendingPlanId ? { pendingPlanId } : {},
      ...pendingCoupon ? { pendingCoupon } : {},
      ...stripeCustomerId ? { stripeCustomerId } : {},
    });

    const request = await this.buildGetRequest(
      `api/payments/subscriptionSummary/${userId}/?${params}`,
    );
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getStripeAccountValidation(planId: string, gymName?: string) {
    const gymNameQuery = gymName ? `?gymName=${gymName}` : '';
    const request = await this.buildGetRequest(
      `api/users/me/stripe-account/${planId}/validation${gymNameQuery}`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async changeUserStripeAccountToAccountWithPlanID(planId: string, gymName: string) {
    const request = await this.buildPostRequest('api/users/me/stripe-account', { planId, gymName });
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getSubscriptionPaymentSummaryForUser() {
    this.logger('[UserServiceClient] get payment summary for user');
    const request = await this.buildGetRequest('api/users/me/subscriptionPaymentSummary');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getCustomerPortalSession(opts?: { useReturnToAppURL?: boolean, returnToBilling?: boolean }) {
    this.logger('[UserServiceClient] get customer portal session for user');
    const request = await this.buildPostRequest('api/users/me/customer-portal-session', opts);
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async createSubscription(
    userId: string,
    planId: string,
    coupon?: string,
    pendingPlanId?: string,
    pendingCoupon?: string,
    stripeCustomerId?: string,
    specialOfferId?: string,
    gymName?: string,
  ) {
    this.logger(`[UserServiceClient] create subscription for user ${userId}`);
    const request = await this.buildPostRequest('api/payments/subscription', {
      userId,
      planId,
      coupon,
      pendingPlanId,
      pendingCoupon,
      stripeCustomerId,
      specialOfferId,
      gymName,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async activatePendingPlan(uid: string, subscriptionId: string) {
    this.logger(`[UserServiceClient] activate elite subscription uid: ${uid}`);
    const request = await this.buildPostRequest('api/users/me/activate-pending-plan', { subscriptionId });
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getWhitelist() {
    this.logger('[UserServiceClient] get whitelist');
    const request = await this.buildGetRequest('api/users/me/whitelist');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async addPartnerToWhitelist(partnerId: string) {
    this.logger(`[UserServiceClient] add ${partnerId} to whitelist`);
    const request = await this.buildPostRequest('api/users/me/whitelist', { partnerId });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async removePartnerFromWhitelist(partnerId: string) {
    this.logger(`[UserServiceClient] remove ${partnerId} from whitelist`);
    const request = await this.buildDeleteRequest(`api/users/me/whitelist/${partnerId}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getBlacklist() {
    this.logger('[UserServiceClient] get blacklist');
    const request = await this.buildGetRequest('api/users/me/blacklist');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async addPartnerToBlacklist(partnerId: string) {
    this.logger(`[UserServiceClient] add ${partnerId} to blacklist`);
    const request = await this.buildPostRequest('api/users/me/blacklist', { partnerId });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async removePartnerFromBlacklist(partnerId: string) {
    this.logger(`[UserServiceClient] remove ${partnerId} from blacklist`);
    const request = await this.buildDeleteRequest(`api/users/me/blacklist/${partnerId}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async searchForUserToAddToWhitelist(partnerIdentifier: string) {
    this.logger(`[UserServiceClient] finding partner ${partnerIdentifier}`);
    const query = `partner=${encodeURIComponent(partnerIdentifier)}`;
    const request = await this.buildGetRequest(`api/users/me/partner-search?${query}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getAllGyms() {
    this.logger('[UserServiceClient] retrieving all gyms');
    const request = await this.buildGetRequest('api/gyms');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public getAppleDeveloperToken = async () => {
    const request = await this.buildGetRequest('api/music/appleDeveloperToken');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public async getUserByEmail(email: string) {
    const result = await fetch(`${this.config.LOCAL_SERVER_BASE_URL}/user/${email}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    const data = await result.json() as any;
    if (result.status === 404) {
      return null;
    }
    return data;
  }

  public async checkInUserbyId(uid: string, allowBagContact: boolean, skippedBiometric: boolean, gymName: string) {
    const result = await fetch(`${this.config.LOCAL_SERVER_BASE_URL}/checkin`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        uid,
        allowBagContact,
        skippedBiometric,
        gymName,
      }),
    });
    if (result.status === 404) {
      throw new Error('No booking found for user.');
    }
    const data = await result.json() as any;
    return data;
  }

  public async signupUser(user: any) {
    this.logger(`[UserServiceClient] signup user ${user.email}`);
    const result = await fetch(`${this.config.fallbackUrl}/user`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        user,
      }),
    });
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async addUserBase64Avatar(base64Image: string) {
    const request = await this.buildPostRequest('api/users/me/avatars', {
      base64Image,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async ptApproveUser(uid: string, ptPin: string) {
    const result = await fetch(`${this.config.fallbackUrl}/user/pt/approvals`, {
      method: 'POST',
      headers: this.getHeaders(this.config.PT_USERNAME, ptPin),
      body: JSON.stringify({
        uid,
      }),
    });
    const data = await result.json() as any;
    if (result.status !== 200) {
      throw new Error(data.message);
    }
    return data;
  }

  public async enrollUserBiometrics(uid: string, base64Image: string) {
    const result = await fetch(`${this.config.fallbackUrl}/user/${uid}/biometrics`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        base64Image,
      }),
    });
    const data = await result.json() as any;
    if (result.status !== 200) {
      throw new Error(data.message);
    }
    return data;
  }

  public async getUserByBiometrics(base64Image: string, gymName: string) {
    const result = await fetch(`${this.config.LOCAL_SERVER_BASE_URL}/user/biometrics/verify`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        base64Image,
        gymName,
      }),
    });
    const data = await result.json() as any;
    if (result.status !== 200) {
      throw new Error(data.message);
    }
    return data;
  }

  // also called by the login terminal, so needs basic auth
  public async getUserBase64Avater(uid: string) {
    const result = await fetch(`${this.config.LOCAL_SERVER_BASE_URL}/user/${uid}/base64Avatar`, {
      method: 'GET',
      headers: this.getHeaders(),
    });
    const data = await result.json() as any;
    if (result.status === 404) {
      throw new Error(data.message);
    }
    return data;
  }

  public async changedPassword() {
    try {
      const request = await this.buildPostRequest('auth/changed', undefined);
      const result = await this.fetch(request.url, request.options);
      const data = await this.assertHttp200AndGetJsonFromResult(result);

      this.logger('[changePassword] inform server of password change result', data);
      return true;
    } catch (err) {
      this.logger('[changePassword] inform server of password change failed', err);
      return false;
    }
  }

  public getBasicAuthUserAndRoles = async () => {
    this.logger('[UserServiceClient] authenticating and getting basic auth user');
    const request = await this.buildGetRequest('user/basic-auth', true);
    const result = await this.fetch(
      request.url,
      this.buildFetchOptionsWithUserProvidedAuth(request.options),
    );
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  };

  public async getMyLastWorkoutToBeReviewed() {
    const request = await this.buildGetRequest('api/users/me/workouts/mostrecent');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async reviewWorkout(userWorkoutSessionId: string, rating: number, comment: string) {
    const request = await this.buildPostRequest(`api/users/me/workouts/${userWorkoutSessionId}/reviews`, {
      rating,
      comment,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    if (rating) {
      this.sendAnalyticsEvent(
        rating.toString(),
        'reviewWorkout-[Sussex]',
        `ID [${userWorkoutSessionId}]`,
      );
    }
    return data;
  }

  public async getMyBodyScans() {
    const request = await this.buildGetRequest('api/users/me/bodyScans');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getBodyScanPdf(scanId: string) {
    const request = await this.buildGetRequest(`api/users/me/bodyScans/${scanId}/report`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async verifyInvitationCode(code: string) {
    const request = await this.buildGetRequest(`api/invitations/${code}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async createSetupIntent(methodTypes: string[], userId: string) {
    const request = await this.buildPostRequest('api/payments/setup-intent', {
      methodTypes, userId,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async createSetupIntentForCustomer(
    userId: string,
    coupon: string,
    onceoff: string,
    planId: string,
    pendingPlanId?: string,
    pendingCoupon?: string,
    specialOfferId?: string,
  ) {
    const request = await this.buildPostRequest('api/payments/setup-intent-customer', {
      userId,
      coupon,
      onceoff,
      planId,
      pendingPlanId,
      pendingCoupon,
      specialOfferId,
    });
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async couponVerify(
    couponCode: string,
    planId: string,
  ): Promise<{ valid: boolean }> {
    const request = await this.buildGetRequest(`api/payments/coupon/${couponCode}/valid?planId=${planId}`);
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async attachPaymentMethodAsDefault(setupIntentId: string, userId: string) {
    const request = await this.buildPostRequest(
      'api/payments/methods/default',
      { setupIntentId, userId },
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getDefaultPaymentMethodInfo(uid: string) {
    this.logger(`[UserServiceClient] get default payment method info uid: ${uid}`);
    const request = await this.buildGetRequest('api/payments/payment-method');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getWorkoutStatsForUser(year: number, month: number) {
    this.logger(`[UserServiceClient] get workout stats for user for ${year}/${month}`);
    const request = await this.buildGetRequest(`api/users/me/workoutStats?year=${year}&month=${month}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getScanStatsForUser(datestamp: number, timezone: string) {
    this.logger(`[UserServiceClient] get scan stats for user for ${datestamp} in ${timezone}`);
    const request = await this.buildGetRequest(`api/users/me/scanStats?datestamp=${datestamp}&timezone=${timezone}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async addBodyAssessmentForUser(assessment: any) {
    this.logger('[UserServiceClient] add body assessment for user');
    const request = await this.buildPostRequest('api/users/me/bodyassessment', { assessment });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async removeBodyScaleForUser() {
    this.logger('[UserServiceClient] delete body assessment for user');
    const request = await this.buildDeleteRequest('api/users/me/bodyScale');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async overrideBodyCompositionForUser(composition: string) {
    this.logger('[UserServiceClient] override body composition for user');
    const request = await this.buildPutRequest('api/users/me/bodycomposition', { composition });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async updateUserProfileColor(profileColor: string) {
    this.logger(`[UserServiceClient] setting user profile color to ${profileColor}`);
    const request = await this.buildPutRequest('api/users/me/profileColor', { profileColor });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async updateUserProfilePin(pin: string) {
    this.logger('[UserServiceClient] setting user profile pin');
    const request = await this.buildPutRequest('api/users/me/pin', { pin });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async addBodyScaleScanForUser(datestamp: number, timezone: string, scanDateString: string, rawScan: any) {
    this.logger('[UserServiceClient] add body scale scan stats for user');
    const request = await this.buildPostRequest('api/users/me/bodyscalescan', {
      datestamp, timezone, scanDateString, rawScan,
    });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async calculateBodyScaleScanStatsForUser(weight: number, bodyFat: number) {
    this.logger('[UserServiceClient] get body scale scan stats for user');
    const request = await
    this.buildGetRequest(`api/users/me/bodyscalescanStats?weight=${weight}&bodyFat=${bodyFat}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getTargetChangeData(requestTime: number, timezone: string) {
    this.logger('[UserServiceClient] get data needed to change target');
    const query = requestTime && timezone
      ? `requestTime=${requestTime}&timezone=${timezone}`
      : '';
    const request = await this.buildGetRequest(
      `api/users/me/targetChangeData?${query}`,
    );
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getOpenInviteLink() {
    this.logger('[UserServiceClient] get open invite link');
    const request = await this.buildGetRequest('api/users/me/open-invite');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getInviteFriendsLink() {
    this.logger('[UserServiceClient] get invite friends link');
    const request = await this.buildGetRequest('api/users/me/invite-friends');
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getWorkoutTileInfo(datestamp: number) {
    this.logger('[UserServiceClient] get next workout for user dashboard');
    const request = await this.buildGetRequest(`api/users/me/dashboard/workoutTile?datestamp=${datestamp}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getAllowDemoBookingsForGym(gymName: string) {
    this.logger('[UserServiceClient] get allow demo bookings for gym');
    const request = await this.buildGetRequest(`api/users/me/allow-demo-booking/${gymName}`);
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getMemberShipPlansUrl(userId: string, gymName?: string) {
    this.logger('[UserServiceClient] get allow demo bookings for gym');
    const params = gymName ? `userId=${userId}&gymName=${gymName}` : `userId=${userId}`;
    const request = await this.buildGetRequest(`api/users/me/membership-plans?${params}`);
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getNextXMeals(numberOfMeals: number, datestamp: number, timeZone: any) {
    this.logger(`[UserServiceClient] get next ${numberOfMeals} meals`);
    const request = await this.buildGetRequest(
      `api/nutrition/nextXMeals?numberOfMeals=${numberOfMeals}&datestamp=${datestamp}&timeZone=${timeZone}`,
    );
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async addDeviceToken(token: string, platform: string) {
    this.logger(`[UserServiceClient] updating user device token ${token} ${platform}`);
    const request = await this.buildPutRequest('api/users/me/devices', { token, platform });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getSignupFlowScreens(signupFlowType: string) {
    this.logger('[UserServiceClient] get signup screens');
    const request = await this.buildGetRequest(`api/signup/flows/${signupFlowType}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async emailAppDownloadLinks() {
    this.logger('[UserServiceClient] get signup screens');
    const request = await this.buildPostRequest('api/users/email-app-links', {});
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async smsAppDownloadLinks(mobileNumber: string) {
    this.logger('[UserServiceClient] get signup screens');
    const request = await this.buildPostRequest('api/users/sms-app-links', { mobileNumber });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getVideoUrlFor(videoName: string) {
    this.logger(`[UserServiceClient] Get video url for: ${videoName}`);
    const request = await this.buildGetRequest(`api/appassets/videos/${videoName}`);
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async contactUs(message: string) {
    this.logger('[UserServiceClient] contact us');
    const request = await this.buildPostRequest('api/users/contact-us', { message });
    const result = await this.fetch(request.url, request.options);
    const data = await this.assertHttp200AndGetJsonFromResult(result);
    return data;
  }

  public async getWeeklyGoalData(requestTime: number, timezone: string) {
    this.logger('[UserServiceClient] Get weekly goal data');
    const request = await this.buildGetRequest(
      `api/users/me/dashboard/weekly-goal?requestTime=${requestTime}&timezone=${timezone}`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getLeaderboardsForUser() {
    this.logger('[UserServiceClient] Get leaderboards for user');
    const request = await this.buildGetRequest('api/users/me/leaderboards');
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getInactiveLeaderboardsForUser(viewOption: 'carousel' | 'all') {
    this.logger('[UserServiceClient] Get inactive leaderboards for user');
    const request = await this.buildGetRequest(
      `api/users/me/leaderboards?leaderboardType=inactive&viewOption=${viewOption}`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getLeaderboardDataForTeam(leaderboardId: string, startDate: Date, endData: Date, team: string) {
    this.logger('[UserServiceClient] Get leaderboard data for team');
    const request = await this.buildGetRequest(
      `api/users/me/leaderboards/${leaderboardId}/${team}?startDate=${startDate}&endDate=${endData}`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getLeaderboardBreakdownForUser(
    userProfileId: string,
    leaderboardConfigId: string,
    startDate: string,
    endDate: string,
    team?: string,
  ) {
    this.logger('[UserServiceClient] Get leaderboard breakdown for user');
    const teamParam = team ? `&team=${team}` : '';
    const request = await this.buildGetRequest(
      'api/users/me/leaderboards/'
      + `${leaderboardConfigId}/user/${userProfileId}?startDate=${startDate}&endDate=${endDate}${teamParam}`,
    );

    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async joinLeaderboard(leaderboardId: string, team?: string) {
    this.logger(`[UserServiceClient] Join leaderboard ${leaderboardId}`);
    const request = await this.buildPostRequest(`api/users/me/leaderboards/${leaderboardId}`, {
      team,
    });
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async optOutOfLeaderboard(leaderboardId: string) {
    this.logger(`[UserServiceClient] Opt out of leaderboard ${leaderboardId}`);
    const request = await this.buildDeleteRequest(`api/users/me/leaderboards/${leaderboardId}`);
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getLeaderboardInstance(leaderboardId: string, startDate: Date, endDate: Date) {
    this.logger(`[UserServiceClient] get leaderboard instance ${leaderboardId}`);
    const request = await this
      .buildGetRequest(`api/users/me/leaderboards/${leaderboardId}?startDate=${startDate}&endDate=${endDate}`);
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getWeeklyWorkoutProgressData(requestTime: number, timezone: string) {
    this.logger('[UserServiceClient] Get weekly workout progress data');
    const request = await this.buildGetRequest(
      `api/users/me/workoutProgress/weekly?requestTime=${requestTime}&timezone=${timezone}`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getWeeklyWorkoutStatsData(requestTime: number, timezone: string) {
    this.logger('[UserServiceClient] Get weekly workout stats data');
    const request = await this.buildGetRequest(
      `api/users/me/workoutStats/weekly?requestTime=${requestTime}&timezone=${timezone}`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getBodyMetricSet(requestTime: number, timezone: string) {
    this.logger('[UserServiceClient] Get body metric data set');
    const request = await this.buildGetRequest(
      `api/users/me/bodyMetricSet?requestTime=${requestTime}&timezone=${timezone}`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getDashboardBlogContent(
    contentType: string,
    longTermGoal: string,
    foodPreference: string,
    count: number,
  ) {
    this.logger(`[UserServiceClient] Get dashboard ${contentType} blog content`);
    const request = await this.buildGetRequest(
      `api/users/me/dashboard/content/${contentType}?longTermGoal=${longTermGoal}`
        + `&foodPreference=${foodPreference}&count=${count}&flagToIncludeHelpWhichWeWillDeprecateInTwoReleases=true`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getTargetAndBodyMetricSetForUser(requestTime: number, timezone: string) {
    this.logger('[UserServiceClient] get target info for user');
    const request = await this.buildGetRequest(
      `api/users/me/targetWeight/info?requestTime=${requestTime}&timezone=${timezone}`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getTargetForUser(requestTime: number, timezone: string) {
    this.logger('[UserServiceClient] get target for user');
    const request = await this.buildGetRequest(
      `api/users/me/target?requestTime=${requestTime}&timezone=${timezone}`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async calculateMinMaxDefaultTarget(requestTime: number, timezone: string, userTargetType: string) {
    this.logger('[UserServiceClient] Calculate min max and default target');
    const query = requestTime && timezone
      ? `requestTime=${requestTime}&timezone=${timezone}&userTargetType=${userTargetType}`
      : '';
    const request = await this.buildGetRequest(
      `api/users/me/targetWeight/minMaxDefault?${query}`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async calculateWeightTarget(
    userInfo: any,
    targetWeight: number,
    requestTime: number,
    timezone: string,
    userTargetType: string,
    targetMetricValues?: any,
    isCurrentMetric?: boolean,
  ) {
    this.logger('[UserServiceClient] Calculate weight target data');
    const request = await this.buildPostRequest(
      `api/users/me/calculateWeightTarget?
        requestTime=${requestTime}&timezone=${timezone}
        &userTargetType=${userTargetType}&isCurrentMetric=${isCurrentMetric}`,
      { userInfo, targetWeight, targetMetricValues },
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async addTargetForUser(
    userInfo: any,
    targetInfo: any,
    milestoneInfo: any,
    requestTime: number,
    timezone: string,
    userTargetType: string,
  ) {
    this.logger('[UserServiceClient] add weight and target for user');
    const request = await this.buildPostRequest(
      `api/users/me/weightTargetInfo?
        requestTime=${requestTime}&timezone=${timezone}&userTargetType=${userTargetType}`,
      { userInfo, targetInfo, milestoneInfo },
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async reviewWeightAndGetTargetInfo(
    reviewedWeight: number,
    PALValue: number,
    requestTime: number,
    timezone: string,
    userTargetType: string,
    rawScan?: any,
    targetMetricValues?: any,
  ) {
    this.logger('[UserServiceClient] Save reviewed weight and get target info');
    const request = await this.buildPostRequest(`api/users/me/reviewedWeight?
        requestTime=${requestTime}&timezone=${timezone}&userTargetType=${userTargetType}`, {
      reviewedWeight, PALValue, rawScan, targetMetricValues,
    });
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async updateMilestoneForUser(milestoneInfo: any) {
    this.logger('[UserServiceClient] Update milestone info for user');
    const request = await this.buildPostRequest('api/users/me/targetWeight/milestone', { milestoneInfo });
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async userTokenSwop(userIdToSwopTokenFor: string, pin: string) {
    this.logger('[UserServiceClient] Try and swop tokens');
    const request = await this.buildPostRequest('api/users/me/tokenSwop', { userIdToSwopTokenFor, pin });
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async resetTargetForUser() {
    this.logger('[UserServiceClient] reset target info for user');
    const request = await this.buildGetRequest(
      'api/users/me/targetWeight/reset',
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getAllUsersInFamilyPlan() {
    this.logger('[UserServiceClient] get all users in family plan');
    const request = await this.buildGetRequest(
      'api/users/family/members',
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async inviteUserToFamilyPlan(userEmailToInvite: string) {
    this.logger('[UserServiceClient] invite user to family plan');
    const request = await this.buildPostRequest('api/users/family/invite', { userEmailToInvite });
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async acceptFamilyPlanInvitation(code: string) {
    this.logger('[UserServiceClient] accept family plan invitation');
    const request = await this.buildPostRequest('api/users/family/join', { code });
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async cancelPendingFamilyPlanInvitation(code: string) {
    this.logger('[UserServiceClient] cancel family plan invitation');
    const request = await this.buildDeleteRequest(
      `api/users/family/invite/${code}`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async removeFamilyMember(userToRemoveId: string) {
    this.logger('[UserServiceClient] get all users in family plan');
    const request = await this.buildDeleteRequest(
      `api/users/family/members/${userToRemoveId}`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getMarketingTileData(uid: string) {
    this.logger('[UserServiceClient] get getmarketingTileData fro user');
    const request = await this.buildGetRequest(
      `api/users/${uid}/marketing-tiles`,
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async completeStrengthAssessment(ratios: any) {
    this.logger('[UserServiceClient] adding strength assessment result for user');
    const request = await this.buildPostRequest('api/users/strength-assessment', { ratios });
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async getStrengthAssessmentConfig() {
    this.logger('[UserServiceClient] getting strength assessment config');
    const request = await this.buildGetRequest(
      'api/users/strength-assessment',
    );
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async updateUserHeartRateMonitors(data: { home?: string, studio?: string }) {
    this.logger('[UserServiceClient] updating user hrm ids');
    const request = await this.buildPostRequest('api/users/me/heartRateMonitors', data);
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async authorizeGarminConnect() {
    this.logger('[UserServiceClient] authorizing user for garmin-connect');
    const request = await this.buildGetRequest('api/users/me/garmin');
    const result = await this.fetch(request.url, request.options);
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  public async completeGarminConnectAuthorization(data: { oauthToken?: string, oauthVerifier?: string }) {
    this.logger('[UserServiceClient] updating user hrm ids');
    const request = await this.buildPostRequest('api/users/me/garmin/WorkoutImportConfig', data);
    const result = await this.fetch(request.url, request.options);
    await this.assertHttp200AndGetJsonFromResult(result);
    return true;
  }
}
